import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// SVG Assets for floating background
const LemonSVG = () => (
  <svg width="50" height="50" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="30" cy="30" r="28" fill="#FFE066" stroke="#E8C020" strokeWidth="2"/>
    <circle cx="30" cy="30" r="21" fill="#FFF4A0" stroke="#E8C020" strokeWidth="1"/>
    <line x1="30" y1="9" x2="30" y2="51" stroke="#D4A800" strokeWidth="1" opacity="0.6"/>
    <line x1="9" y1="30" x2="51" y2="30" stroke="#D4A800" strokeWidth="1" opacity="0.6"/>
    <line x1="15" y1="15" x2="45" y2="45" stroke="#D4A800" strokeWidth="1" opacity="0.6"/>
    <line x1="45" y1="15" x2="15" y2="45" stroke="#D4A800" strokeWidth="1" opacity="0.6"/>
  </svg>
);

const MintLeafSVG = () => (
  <svg width="36" height="50" viewBox="0 0 40 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 52 C20 52 2 38 2 20 C2 9 11 2 20 2 C29 2 38 9 38 20 C38 38 20 52 20 52Z" fill="#5DB87A" stroke="#3D8A5A" strokeWidth="1.2"/>
    <line x1="20" y1="4" x2="20" y2="52" stroke="#3D8A5A" strokeWidth="1" opacity="0.5"/>
    <line x1="11" y1="14" x2="29" y2="28" stroke="#3D8A5A" strokeWidth="0.8" opacity="0.4"/>
    <line x1="9" y1="25" x2="31" y2="36" stroke="#3D8A5A" strokeWidth="0.8" opacity="0.4"/>
  </svg>
);

const SparkleSVG = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0 L15 9 L24 12 L15 15 L12 24 L9 15 L0 12 L9 9 Z" fill="#FACF7A"/>
  </svg>
);

// HTML/CSS Cocktail Glass Component based on your reference
const CSSCocktail = ({ scale = 1, style = {} }) => {
  return (
    <div 
      className="cocktail-wrapper" 
      style={{ 
        transform: `scale(${scale})`, 
        transformOrigin: "bottom center",
        width: "200px",
        height: "220px",
        position: "relative",
        display: "inline-block",
        ...style 
      }}
    >
      <div className="cocktail-table">
        <div className="glassHead">
          <div className="strawContainer">
            <div className="straw"></div>
            <div className="lemon"></div>
            <div className="ice"></div>
            <div className="ice ice2"></div>
            <div className="ice ice3"></div>
          </div>
        </div>
        <div className="liquid2"></div>
        <div className="liquid"></div>
        <div className="glassBar"></div>
        <div className="glassFoot"></div>
      </div>
    </div>
  );
};

// Floating Animation Configs - Distributed nicely across the vertical height
// Low percentages (under 15%) place elements directly in the Hero Section / first fold!
const floatingParticles = [
  // --- HERO SECTION / FIRST FOLD (0% to 15%) ---
  { id: 1, type: "cocktail", top: "7%", left: "4%", scale: 0.35, opacity: 0.22, xDist: 15, yDist: 15, rotateDist: 10, duration: 9 }, // Hero left
  { id: 2, type: "lemon", top: "8%", right: "15%", scale: 0.8, opacity: 0.16, xDist: -15, yDist: -10, rotateDist: 25, duration: 8 }, 
  { id: 3, type: "sparkle", top: "4%", left: "20%", scale: 0.9, opacity: 0.2, xDist: 8, yDist: -8, rotateDist: 90, duration: 5 },
  { id: 4, type: "sparkle", top: "12%", right: "25%", scale: 0.75, opacity: 0.18, xDist: -8, yDist: 8, rotateDist: -90, duration: 7 },
  { id: 5, type: "mint", top: "14%", left: "8%", scale: 0.85, opacity: 0.14, xDist: 10, yDist: 15, rotateDist: -15, duration: 10 },
  { id: 15, type: "cocktail", top: "11%", right: "8%", scale: 0.28, opacity: 0.2, xDist: -10, yDist: 12, rotateDist: -30, duration: 11 }, // Tilted Hero right
  { id: 16, type: "cocktail", top: "4%", right: "5%", scale: 0.3, opacity: 0.22, xDist: 12, yDist: -10, rotateDist: 15, duration: 10 }, // Hero right top!

  // --- SECOND FOLD & MIDDLE SECTIONS (15% to 50%) ---
  { id: 6, type: "cocktail", top: "22%", right: "10%", scale: 0.32, opacity: 0.2, xDist: -15, yDist: 20, rotateDist: -15, duration: 11 },
  { id: 7, type: "mint", top: "28%", left: "6%", scale: 0.9, opacity: 0.15, xDist: 20, yDist: -15, rotateDist: 25, duration: 9 },
  { id: 8, type: "sparkle", top: "34%", right: "15%", scale: 0.85, opacity: 0.22, xDist: 10, yDist: -12, rotateDist: 120, duration: 6 },
  { id: 9, type: "lemon", top: "42%", left: "5%", scale: 0.75, opacity: 0.16, xDist: -10, yDist: 25, rotateDist: 35, duration: 8 },

  // --- LOWER SECTIONS (50% to 100%) ---
  { id: 10, type: "cocktail", top: "54%", left: "7%", scale: 0.35, opacity: 0.22, xDist: 12, yDist: -15, rotateDist: 20, duration: 9 },
  { id: 11, type: "mint", top: "68%", right: "7%", scale: 0.85, opacity: 0.14, xDist: -18, yDist: 12, rotateDist: -30, duration: 11 },
  { id: 12, type: "lemon", top: "76%", left: "4%", scale: 0.7, opacity: 0.18, xDist: 20, yDist: -10, rotateDist: 45, duration: 7 },
  { id: 13, type: "sparkle", top: "82%", right: "12%", scale: 1, opacity: 0.2, xDist: -8, yDist: 10, rotateDist: 180, duration: 6 },
  { id: 14, type: "cocktail", top: "90%", right: "8%", scale: 0.32, opacity: 0.2, xDist: -10, yDist: -15, rotateDist: -10, duration: 10 }
];

const renderParticle = (p) => {
  switch (p.type) {
    case "lemon": return <LemonSVG />;
    case "mint": return <MintLeafSVG />;
    case "cocktail": return <CSSCocktail scale={1} />; // inherits scale from the motion.div wrapper!
    default: return <SparkleSVG />;
  }
};

const SummerThemeWrapper = ({ children, hideCountdown = false, hideHeroParticles = false }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: false });
  const [isDismissed, setIsDismissed] = useState(true);

  // Target event: Friday, 26 June 2026, 18:00 BST.
  const targetDate = new Date("2026-06-26T18:00:00+01:00");

  useEffect(() => {
    // Check local storage dismissal
    const dismissed = localStorage.getItem("summerMeet2026Dismissed");
    if (dismissed !== "true") {
      setIsDismissed(false);
    }

    const calculateTimeLeft = () => {
      const difference = +targetDate - +new Date();
      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        isExpired: false
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    localStorage.setItem("summerMeet2026Dismissed", "true");
    setIsDismissed(true);
  };

  return (
    <div className="summer-theme-container" style={{ position: "relative", minHeight: "100vh" }}>
      {/* Scope CSS */}
      <style>{`
        .summer-theme-container {
          background-color: #ffffff;
          position: relative;
          min-height: 100vh;
        }

        /* Force transparent backgrounds on specific white/light sections of the homepage so the warmth overlay is visible */
        .summer-theme-container section.photo_gallery_section,
        .summer-theme-container section.discussion_panelists_section,
        .summer-theme-container section.funfact_section,
        .summer-theme-container section.pricing_section,
        .summer-theme-container section.service_section,
        .summer-theme-container section.contact_section,
        .summer-theme-container .bg-white:not(.meet-details-box) {
          background: transparent !important;
          background-color: transparent !important;
        }

        .summer-warmth-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          background: 
            radial-gradient(circle at 80% 5%, rgba(255, 170, 0, 0.08) 0%, rgba(251, 168, 52, 0.03) 40%, transparent 80%),
            radial-gradient(circle at 15% 50%, rgba(232, 133, 26, 0.04) 0%, transparent 60%),
            radial-gradient(circle at 70% 85%, rgba(255, 224, 102, 0.05) 0%, transparent 70%);
          z-index: 1;
        }

        .summer-particle {
          position: absolute;
          pointer-events: none;
          z-index: 999; /* Higher z-index to overlay on top of section backgrounds */
        }

        /* --- COCKTAIL GLASS STYLING (THEMED AND HIGH VISIBILITY) --- */
        .cocktail-table {
          position: absolute;
          background-color: transparent; 
          bottom: 10px;
          height: 20px;
          width: 100%;
          border-radius: 20%;
        }

        .glassFoot {
          position: absolute;
          bottom: 15px;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 12px;
          border-radius: 60% 60% 10% 10%;
          background: rgba(255, 255, 255, 0.7); 
          border: 2px solid rgba(232, 133, 26, 0.5); /* Clear themed amber outline */
        }

        .glassBar {
          position: absolute;
          bottom: 25px;
          left: 50%;
          transform: translateX(-50%);
          width: 10px;
          height: 70px;
          background: rgba(255, 255, 255, 0.7); 
          border-left: 2px solid rgba(232, 133, 26, 0.5);
          border-right: 2px solid rgba(232, 133, 26, 0.5);
        }

        .glassHead {
          position: absolute;
          bottom: 85px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 100px 100px 0 100px;
          border-color: rgba(232, 133, 26, 0.45) transparent rgba(255, 255, 255, 0.3) transparent;
          z-index: 2;
        }

        .liquid {
          position: absolute;
          bottom: 85px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 50px 50px 0 50px;
          border-color: #E8851A transparent rgba(255, 255, 255, 0.1) transparent; /* Theme orange */
          animation-duration: 3s;
          animation-name: changeBorderWidth;
          animation-iteration-count: infinite;
          animation-direction: alternate;
          z-index: 1;
        }

        .liquid2 {
          position: absolute;
          bottom: 85px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 50px 50px 0 50px;
          border-color: #FBA834 transparent rgba(255, 255, 255, 0.1) transparent; /* Theme amber */
          animation-duration: 3s;
          animation-name: changeBorderWidth2;
          animation-iteration-count: infinite;
          animation-direction: alternate;
          z-index: 1;
        }

        .strawContainer {
          position: relative;
          width: 20px;
          height: 20px;
        }

        .straw {
          position: absolute;
          right: 300%;
          bottom: 100%;
          width: 10px;
          height: 150px;
          transform: rotate(-45deg);
          background: linear-gradient(to left top, #FFE066, #FBA834);
          border-radius: 4px;
        }

        .straw::before {
          position: absolute;
          width: 10px;
          content: "";
          height: 70px;
          border-radius: 70% 70% 0% 0%;
          transform: rotate(80deg);
          background: linear-gradient(to left top, #FFE066, #FBA834);
          right: 30px;
          top: -30px;
        }

        .ice {
          position: absolute;
          bottom: 350%;
          right: 30%;
          transform: rotate(30deg);
          box-shadow: 0 4px 10px rgba(232, 133, 26, 0.15);
          width: 36px;
          height: 36px;
          background-color: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(232, 133, 26, 0.2);
          opacity: 0.8;
          border-radius: 30%;
          animation-duration: 3s;
          animation-name: changeRotateIce2;
          animation-iteration-count: infinite;
          animation-direction: alternate;
          z-index: 3;
        }

        .ice.ice2 {
          left: 70%;
          transform: rotate(10deg);
        }

        .ice.ice3 {
          left: 0%;
          bottom: 500%;
          transform: rotate(-20deg);
          animation-duration: 3s;
          animation-name: changeRotateIce;
          animation-iteration-count: infinite;
          animation-direction: alternate;
        }

        .lemon {
          border: 5px solid #dcd000;
          width: 50px;
          height: 20px;
          background: #FFE066;
          border-radius: 0 0 100px 100px;
          z-index: 4;
          margin: 0 auto;
          position: relative;
          left: 60px;
          bottom: 120px;
          transform: rotate(230deg);
        }

        @keyframes changeBorderWidth {
          from { border-width: 70px 70px 0 70px; }
          to { border-width: 30px 30px 0 30px; }
        }

        @keyframes changeBorderWidth2 {
          from { border-width: 90px 90px 0 90px; }
          to { border-width: 50px 50px 0 50px; }
        }

        @keyframes changeRotateIce {
          from { transform: rotate(-20deg); }
          to { transform: rotate(-40deg); }
        }

        @keyframes changeRotateIce2 {
          from { transform: rotate(-30deg); }
          to { transform: rotate(30deg); }
        }

        /* --- COUNTDOWN WIDGET STYLING --- */
        .countdown-widget-wrap {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9999;
          font-family: 'Inter', sans-serif;
        }

        .countdown-card {
          background: rgba(255, 248, 237, 0.88);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border: 1.5px solid #FACF7A;
          border-radius: 20px;
          padding: 18px 24px 18px 20px;
          box-shadow: 0 10px 30px rgba(232, 133, 26, 0.15), 0 1px 3px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 330px;
          position: relative;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .countdown-card:hover {
          border-color: #E8851A;
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(232, 133, 26, 0.22);
        }

        .countdown-header {
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px dashed rgba(245, 200, 112, 0.6);
          padding-bottom: 8px;
        }

        .countdown-icon-container {
          background: rgba(254, 235, 184, 0.7);
          border-radius: 50%;
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: visible;
          position: relative;
        }

        .countdown-title {
          font-size: 13px;
          font-weight: 700;
          color: #B87A10;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .countdown-subtitle {
          font-size: 11px;
          color: #7A5E30;
          font-weight: 500;
          margin-top: 1px;
        }

        .countdown-grid {
          display: flex;
          gap: 12px;
          align-items: center;
          margin: 4px 0;
        }

        .countdown-item {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .countdown-num {
          font-size: 20px;
          font-weight: 800;
          color: #1A1A1A;
          line-height: 1.1;
        }

        .countdown-lbl {
          font-size: 9px;
          text-transform: uppercase;
          color: #9A7020;
          font-weight: 600;
          letter-spacing: 0.2px;
          margin-top: 2px;
        }

        .countdown-separator {
          font-size: 18px;
          font-weight: 700;
          color: #FACF7A;
          margin-top: -12px;
        }

        .countdown-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-top: 2px;
        }

        .countdown-btn {
          flex: 1;
          background: #E8851A;
          color: #fff;
          border: none;
          padding: 8px 16px;
          font-size: 12px;
          font-weight: 700;
          border-radius: 30px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          text-align: center;
          box-shadow: 0 4px 10px rgba(232,133,26,0.25);
        }

        .countdown-btn:hover {
          background: #C56B0A;
          box-shadow: 0 6px 14px rgba(232,133,26,0.35);
          transform: scale(1.02);
        }

        .countdown-close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: transparent;
          border: none;
          color: #B87A10;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .countdown-close:hover {
          background: rgba(232, 133, 26, 0.1);
          color: #E8851A;
        }

        /* Responsive */
        @media (max-width: 576px) {
          .countdown-widget-wrap {
            display: none !important;
          }
        }
      `}</style>

      {/* Summer Warmth Backdrop Layer */}
      <div className="summer-warmth-overlay" />

      {/* Floating Elements Background Loop */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, overflow: "hidden", pointerEvents: "none", zIndex: 999 }}>
        {(hideHeroParticles
          ? floatingParticles.filter((p) => parseFloat(p.top) >= 18)
          : floatingParticles
        ).map((p) => (
          <motion.div
            key={p.id}
            className="summer-particle"
            style={{
              top: p.top,
              left: p.left,
              right: p.right,
              scale: p.scale,
              opacity: p.opacity,
            }}
            animate={{
              x: [0, p.xDist, 0],
              y: [0, p.yDist, 0],
              rotate: [0, p.rotateDist, 0],
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {renderParticle(p)}
          </motion.div>
        ))}
      </div>

      {/* Main Homepage Contents */}
      <div style={{ position: "relative", zIndex: 2 }}>
        {children}
      </div>

      {/* Sticky Countdown Widget */}
      <AnimatePresence>
        {!isDismissed && !timeLeft.isExpired && !hideCountdown && (
          <motion.div
            className="countdown-widget-wrap"
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 25 }}
          >
            <div className="countdown-card">
              <button className="countdown-close" onClick={handleDismiss} title="Dismiss Widget">
                ✕
              </button>
              
              <div className="countdown-header">
                <div className="countdown-icon-container">
                  {/* Scaled animated CSS cocktail glass centered inside circle container without cutting off stem/foot */}
                  <CSSCocktail 
                    scale={0.24} 
                    style={{ 
                      position: "absolute",
                      bottom: "-3px", // Align base of glass inside circle boundaries
                      left: "50%",
                      transform: "translateX(-50%) scale(0.24)",
                      transformOrigin: "bottom center"
                    }} 
                  />
                </div>
                <div>
                  <div className="countdown-title">Summer Members Meet</div>
                  <div className="countdown-subtitle">Friday, 26 June • Rooftop Bar</div>
                </div>
              </div>

              <div className="countdown-grid">
                <div className="countdown-item">
                  <span className="countdown-num">{String(timeLeft.days).padStart(2, '0')}</span>
                  <span className="countdown-lbl">Days</span>
                </div>
                <span className="countdown-separator">:</span>
                <div className="countdown-item">
                  <span className="countdown-num">{String(timeLeft.hours).padStart(2, '0')}</span>
                  <span className="countdown-lbl">Hours</span>
                </div>
                <span className="countdown-separator">:</span>
                <div className="countdown-item">
                  <span className="countdown-num">{String(timeLeft.minutes).padStart(2, '0')}</span>
                  <span className="countdown-lbl">Mins</span>
                </div>
                <span className="countdown-separator">:</span>
                <div className="countdown-item">
                  <span className="countdown-num">{String(timeLeft.seconds).padStart(2, '0')}</span>
                  <span className="countdown-lbl">Secs</span>
                </div>
              </div>

              <div className="countdown-actions">
                <a 
                  className="countdown-btn" 
                  href="/summer-meet-2026"
                >
                  Register Now ✦
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SummerThemeWrapper;
